import { GAMEPAD, PAD_BUTTONS } from "../config";
import type { GameAction } from "../core/types";

const DIRECTION_ACTIONS: readonly GameAction[] = ["up", "down", "left", "right"];

interface PadButtonMap {
  ok: number[];
  back: number[];
  sound: number[];
}

/** Xbox eşlemesi: A/Start onay, B/Back geri, Y/X ses. */
const BUTTON_MAP: PadButtonMap = {
  ok: [PAD_BUTTONS.A, PAD_BUTTONS.START],
  back: [PAD_BUTTONS.B, PAD_BUTTONS.BACK],
  sound: [PAD_BUTTONS.Y, PAD_BUTTONS.X],
};

/**
 * Xbox controller monitörü — her karede `poll()` çağrılır.
 * D-pad: edge + basılı tutma tekrarı (menü gezinme için).
 * Sol analog: yön değiştiğinde tetiklenir (dead-zone'lu).
 */
export class GamepadMonitor {
  private readonly dispatch: (action: GameAction) => void;
  private readonly pressedAt = new Map<GameAction, number | null>();
  private readonly repeatCount = new Map<GameAction, number>();
  private lastAxisDir: GameAction | null = null;

  constructor(dispatch: (action: GameAction) => void) {
    this.dispatch = dispatch;
    for (const action of DIRECTION_ACTIONS) {
      this.pressedAt.set(action, null);
      this.repeatCount.set(action, 0);
    }
  }

  poll(now: number): void {
    const pads = navigator.getGamepads?.() ?? [];
    for (const pad of pads) {
      if (!pad) continue;
      this.pollButtons(pad, now);
      this.pollAxis(pad);
      return; // ilk aktif kol yeterli
    }
  }

  private pollButtons(pad: Gamepad, now: number): void {
    const active = new Set<GameAction>();
    const entries = Object.entries(BUTTON_MAP) as Array<[GameAction, number[]]>;
    for (const [action, indices] of entries) {
      if (indices.some((i: number) => pad.buttons[i]?.pressed)) active.add(action);
    }
    for (const action of DIRECTION_ACTIONS) {
      if (pad.buttons[this.dpadIndex(action)]?.pressed) active.add(action);
    }

    for (const action of [...DIRECTION_ACTIONS, "ok", "back"] as const) {
      const isActive = active.has(action);
      const pressedAt = this.pressedAt.get(action) ?? null;

      if (isActive && pressedAt === null) {
        this.pressedAt.set(action, now);
        this.repeatCount.set(action, 0);
        this.dispatch(action);
      } else if (isActive && pressedAt !== null) {
        // Basılı tutunca tekrar: yalnızca yön butonları (ok/back tek atımlı kalsın)
        if (!DIRECTION_ACTIONS.includes(action)) continue;
        const heldFor = now - pressedAt;
        if (heldFor <= GAMEPAD.repeatDelayMs) continue;
        const count = Math.floor((heldFor - GAMEPAD.repeatDelayMs) / GAMEPAD.repeatRateMs);
        if (count > (this.repeatCount.get(action) ?? 0)) {
          this.repeatCount.set(action, count);
          this.dispatch(action);
        }
      } else if (!isActive) {
        this.pressedAt.set(action, null);
        this.repeatCount.set(action, 0);
      }
    }
  }

  private pollAxis(pad: Gamepad): void {
    const ax = pad.axes[0] ?? 0;
    const ay = pad.axes[1] ?? 0;
    const { deadZone } = GAMEPAD;

    let axisDir: GameAction | null = null;
    if (Math.abs(ax) > deadZone || Math.abs(ay) > deadZone) {
      axisDir =
        Math.abs(ax) > Math.abs(ay)
          ? ax > 0
            ? "right"
            : "left"
          : ay > 0
            ? "down"
            : "up";
    }

    if (axisDir !== this.lastAxisDir) {
      this.lastAxisDir = axisDir;
      if (axisDir) this.dispatch(axisDir);
    }
  }

  private dpadIndex(action: GameAction): number {
    switch (action) {
      case "up":
        return PAD_BUTTONS.DPAD_UP;
      case "down":
        return PAD_BUTTONS.DPAD_DOWN;
      case "left":
        return PAD_BUTTONS.DPAD_LEFT;
      case "right":
        return PAD_BUTTONS.DPAD_RIGHT;
      default:
        return -1;
    }
  }
}
